import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import LanguageSwitcher from './LanguageSwitcher';
import { 
  Brain, LayoutDashboard, MessageCircle, Sparkles, 
  Target, BookOpen, BarChart3, User, LogOut, Menu, X, Trophy, Crown 
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '../lib/utils';

const getNavItems = (t) => [
  { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard'), requiresPremium: false },
  { to: '/coach', icon: MessageCircle, label: t('nav.coach'), requiresPremium: true },
  { to: '/meditations', icon: Brain, label: t('nav.meditations'), requiresPremium: true },
  { to: '/goals', icon: Target, label: t('nav.goals'), requiresPremium: false },
  { to: '/journal', icon: BookOpen, label: t('nav.journal'), requiresPremium: false },
  { to: '/affirmations', icon: Sparkles, label: t('nav.affirmations'), requiresPremium: false },
  { to: '/achievements', icon: Trophy, label: t('nav.achievements'), requiresPremium: true },
  { to: '/analytics', icon: BarChart3, label: t('nav.analytics'), requiresPremium: true },
  { to: '/subscription', icon: Crown, label: 'Subscription', highlight: true, requiresPremium: false },
];

export default function Layout({ children }) {
  const { user, logout, isFreeTierExhausted, premiumStatus } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navItems = getNavItems(t);

  // Check if user should be redirected to subscription
  const handleNavClick = (e, item) => {
    if (item.requiresPremium && isFreeTierExhausted() && !premiumStatus?.is_premium) {
      e.preventDefault();
      navigate('/subscription');
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex flex-col w-64 border-r border-border bg-card/30 p-6">
        <div className="flex items-center justify-between mb-10">
          <Link to="/dashboard" className="flex items-center gap-2">
            <Brain className="h-8 w-8 text-primary" />
            <span className="text-xl font-serif font-bold text-foreground">MindForge</span>
          </Link>
          <LanguageSwitcher variant="minimal" />
        </div>
        
        <nav className="flex-1 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={(e) => handleNavClick(e, item)}
              className={cn(
                "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                location.pathname === item.to
                  ? "bg-primary/10 text-primary border border-primary/20"
                  : item.highlight
                  ? "text-primary bg-primary/5 hover:bg-primary/10 border border-primary/20"
                  : "text-muted-foreground hover:text-foreground hover:bg-white/5"
              )}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className={cn("h-5 w-5", item.highlight && "text-primary")} />
              <span className="font-medium">{item.label}</span>
              {item.highlight && (
                <span className="ml-auto text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">PRO</span>
              )}
            </Link>
          ))}
        </nav>
        
        <div className="border-t border-border pt-6 mt-6 space-y-2">
          <Link
            to="/profile"
            className={cn(
              "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
              location.pathname === '/profile'
                ? "bg-primary/10 text-primary border border-primary/20"
                : "text-muted-foreground hover:text-foreground hover:bg-white/5"
            )}
            data-testid="nav-profile"
          >
            <User className="h-5 w-5" />
            <span className="font-medium">{t('common.profile')}</span>
          </Link>
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all duration-200"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5" />
            <span className="font-medium">{t('common.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border">
        <div className="flex items-center justify-between p-4">
          <Link to="/dashboard" className="flex items-center gap-2">
            <Brain className="h-7 w-7 text-primary" />
            <span className="text-lg font-serif font-bold text-foreground">MindForge</span>
          </Link>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="bg-primary/20 border-primary/50 hover:bg-primary/30 text-primary"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
        
        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav className="absolute top-full left-0 right-0 bg-background border-b border-border p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setMobileMenuOpen(false)}
                className={cn(
                  "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200",
                  location.pathname === item.to
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground"
                )}
              >
                <item.icon className="h-5 w-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
            <Link
              to="/profile"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-4 py-3 rounded-xl text-muted-foreground"
            >
              <User className="h-5 w-5" />
              <span className="font-medium">{t('common.profile')}</span>
            </Link>
            <button
              onClick={() => { logout(); setMobileMenuOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-destructive"
            >
              <LogOut className="h-5 w-5" />
              <span className="font-medium">{t('common.logout')}</span>
            </button>
            <div className="pt-4 border-t border-border mt-2">
              <LanguageSwitcher />
            </div>
          </nav>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 lg:p-8 p-4 pt-20 lg:pt-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
}
